import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, Circle, RefreshCw } from 'lucide-react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components';
import { type BillingScheinStatus, ScheinType } from 'types/billingScheinStatus';

export type BillingScheinDialogMode = 'no_schein' | 'wrong_type';

interface BillingScheinDialogProps {
  mode: BillingScheinDialogMode;
  scheinStatus: BillingScheinStatus;
  isRechecking?: boolean;
  onClose: () => void;
  onRecheck: () => void;
  onConfirm: () => void;
}

function describeScheinType(type: ScheinType, t: (key: string) => string): string {
  return t(`result.billingScheinDialog.scheinType.${type}`);
}

export function BillingScheinDialog({
  mode,
  scheinStatus,
  isRechecking = false,
  onClose,
  onRecheck,
  onConfirm,
}: BillingScheinDialogProps) {
  const { t } = useTranslation();
  const [confirmAcknowledged, setConfirmAcknowledged] = useState(false);
  const existingTypesKey = scheinStatus.existingTypes.join(',');

  useEffect(() => {
    if (mode === 'wrong_type') setConfirmAcknowledged(false);
  }, [mode, existingTypesKey]);

  if (mode === 'no_schein') {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('result.billingScheinDialog.noScheinTitle')}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 text-destructive font-medium text-sm">
            <AlertCircle className="size-5 shrink-0" aria-hidden />
            <span>{t('result.billingScheinDialog.noScheinHeadline')}</span>
          </div>
          <DialogDescription>
            {t('result.billingScheinDialog.noScheinDescription')}
          </DialogDescription>
          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>
              {t('general.dismiss')}
            </Button>
            <Button onClick={onRecheck} disabled={isRechecking}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${isRechecking ? 'animate-spin' : ''}`} />
              {t('result.billingScheinDialog.recheck')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const existingType = scheinStatus.existingTypes[0] ?? ScheinType.Other;
  const existingLabel = describeScheinType(existingType, t);
  const expectedLabel = describeScheinType(scheinStatus.expectedType, t);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('result.billingScheinDialog.wrongTypeTitle')}</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {t('result.billingScheinDialog.wrongTypeDescription')}
        </DialogDescription>
        <div className="rounded-md border border-border p-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <span className="text-muted-foreground">
            {t('result.billingScheinDialog.existingLabel')}
          </span>
          <span className="flex items-center gap-2 font-medium text-amber-700">
            <AlertTriangle className="size-4 shrink-0" aria-hidden />
            {existingLabel}
          </span>
          <span className="text-muted-foreground">
            {t('result.billingScheinDialog.expectedLabel')}
          </span>
          <span className="flex items-center gap-2 font-medium">
            <Circle className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            {expectedLabel}
          </span>
        </div>
        <div className="flex items-start gap-2">
          <Checkbox
            checked={confirmAcknowledged}
            onCheckedChange={(checked) => setConfirmAcknowledged(checked === true)}
            label={t('result.billingScheinDialog.confirmAcknowledge', {
              existing: existingLabel,
            })}
          />
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('result.billingScheinDialog.cancel')}
          </Button>
          <Button onClick={onConfirm} disabled={!confirmAcknowledged}>
            {t('result.billingScheinDialog.continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
